import { GenericDialog } from "components/common/dialogs/GenericDialog";
import OverlaySpinner from "components/common/loaders/OverlaySpinner";
import Price from "components/common/Price";
import GiftCardDialog from "components/payments/dialogs/GiftCardDialog";
import { AppliedGiftCardsType } from "lib/contexts/CheckoutProvider";
import { deleteRequest } from "lib/core/apiClient";
import { classNames, maskString } from "lib/utils/helpers";
import { errorToast } from "lib/utils/toasters";
import React, { useMemo, useState } from "react";
import { XCircle, CaretRight, Keyboard } from "@phosphor-icons/react";
import { mutate } from "swr";
import { usePaymentContext } from "lib/contexts/PaymentProvider";
import { getUnApplicablePaymentReason, parseMethodUiAttributes } from "lib/utils/payments";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { apiURI } from "lib/utils/constants";
import { useLocale } from "lib/hooks/useLocale";
import SuggestedMethodWrapper from "components/common/SuggestedMethodWrapper";
import LogosStack from "components/common/LogosStack";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { PaymentMode, PaymentModeType } from "lib/types/payments";
import { usePopupOrchestrator } from "lib/contexts/PopupOrchestratorProvider";

interface GiftCardProps {}

const GiftCard: React.FC<GiftCardProps> = ({}) => {
  const { t } = useLocale();
  const { openPopup } = usePopupOrchestrator();
  const {
    state: { appliedGiftCards, checkoutId, isC2P, hasDefaultShippingHandleSelected },
    actions: { updateCheckoutBasedOnCheckoutResponse },
  } = useCheckoutContext();
  
  const {
    state: { paymentMethods },
    actions: { handleUnapplicablePaymentMethod, mutatePayment },
  } = usePaymentContext();
  const {
    state: { checkoutMetadata },
  } = useMerchantContext();

  const gcModeConfig = paymentMethods?.find((e: PaymentMode) => e.name === "GC");
  const unapplicabilityReason = getUnApplicablePaymentReason(
    gcModeConfig?.unavailabilityReasons || [],
    gcModeConfig?.metadata?.title || "",
  );

  const methodUiAttributes = useMemo(() => {
    return parseMethodUiAttributes({mode: gcModeConfig, checkoutMetadata});
  }, [gcModeConfig, checkoutMetadata]);

  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleDeleteGiftCard = async (giftCardToBeDeleted: string) => {
    if (!appliedGiftCards?.find((giftCard) => giftCard?.cardId === giftCardToBeDeleted)) return;

    try {
      setIsLoading(true);
      const payload = {
        src_reference_id: giftCardToBeDeleted,
      };
      const response = await deleteRequest(`/checkout/v1/checkout/${checkoutId}/rewards/gc/remove`, payload);
      handleCheckoutResponse(response);
      mutatePayment();
      mutate(`/checkout/${checkoutId}/rewards`);
    } catch (e) {
      errorToast(t("delete_gift_card_error"));
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckoutResponse = (checkoutResponse: any) => {
    updateCheckoutBasedOnCheckoutResponse(checkoutResponse);
  };

  const handleGCClick = () => {
    if (!gcModeConfig?.isAvailable) {
      handleUnapplicablePaymentMethod(gcModeConfig?.name || "GC", gcModeConfig?.unavailabilityReasons || []);
      return;
    }
    if (!hasDefaultShippingHandleSelected) {
      openPopup("SHIPPING_HANDLES", {});
      return;
    }
    setOpenDialog(true);
  };

  const renderGiftCardMethod = () => {
    return (
      <div
        className={classNames(
          "border rounded-2xl overflow-hidden",
          gcModeConfig?.isAvailable ? "hover:shadow-[0_0_0_100px_rgba(113,113,123,0.15)_inset]" : "",
        )}
        style={{
          borderColor: methodUiAttributes.paymentCard.borderColor,
          backgroundColor: methodUiAttributes.paymentCard.backgroundColor,
        }}>
        <div className={`group flex items-center px-3.5 py-2.5  gap-3 cursor-pointer w-full min-h-14`}>
          {methodUiAttributes.layout === "ICONS" && (
            <div className="flex p-1">
              <Keyboard size={28} weight="duotone" color={methodUiAttributes.paymentCard.iconColor} />
            </div>
          )}

          <div className="flex w-full flex-1 flex-col items-start justify-start space-y-1">
            <div className="flex gap-1 items-center">
              <h3
                className="text-sm font-medium"
                style={{
                  color: gcModeConfig?.isAvailable ? methodUiAttributes.paymentCard.textColor : "#949494",
                }}>
                {(methodUiAttributes.title || gcModeConfig?.metadata?.title) ?? ""}
              </h3>
              {methodUiAttributes.layout === "LOGOS" && <LogosStack logos={methodUiAttributes.icons} />}
            </div>
            {!methodUiAttributes.paymentCard.isSubtitleHidden && (
              <p
                className="text-left text-xs font-normal"
                style={{ color: methodUiAttributes.paymentCard.subtitleColor }}>
                {(methodUiAttributes.subtitle || gcModeConfig?.metadata?.description) ?? ""}
              </p>
            )}
          </div>

          <CaretRight className="h-4 w-4" style={{ color: methodUiAttributes.paymentCard.textColor }} />
        </div>
        {appliedGiftCards?.length > 0 && (
          <div className="flex w-full flex-col bg-yay-lighter overflow-hidden">
            {appliedGiftCards.map((giftCard: AppliedGiftCardsType) => {
              return (
                <div className="border-box flex w-full flex-row px-3 py-3" key={giftCard?.cardId}>
                  <div className="flex w-full flex-row items-center justify-between overflow-hidden pr-2">
                    <h3 className="flex w-full items-center space-x-1 truncate pr-2 text-sm font-normal text-coal-light">
                      <p className="truncate text-sm font-medium uppercase text-coal-dark">
                        {maskString(giftCard?.displayName, 9, 5, "•")}
                      </p>
                    </h3>
                    <p className="inline-flex w-1/2 justify-end overflow-hidden text-sm font-medium text-yay-dark">
                      <Price total={giftCard?.cardValue} isDiscounted={true} />
                    </p>
                  </div>
                  {!isC2P && (
                    <button
                      className="flex h-4 w-4 items-center justify-center"
                      onClick={() => handleDeleteGiftCard(giftCard?.cardId)}>
                      <XCircle className="h-4 w-4 text-coal-dark" strokeWidth={2.5} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <SuggestedMethodWrapper
        isSuggested={methodUiAttributes.isSuggested}
        title={methodUiAttributes.suggestedTile?.title}
        backgroundColor={methodUiAttributes.suggestedTile?.backgroundColor}
        textColor={methodUiAttributes.suggestedTile?.textColor}>
        <button className="w-full" disabled={!gcModeConfig?.isAvailable} onClick={handleGCClick}>
          {renderGiftCardMethod()}
          {!gcModeConfig?.isAvailable && unapplicabilityReason && (
            <div className="bg-gray-lightest">
              <p className="px-3 py-2 text-left text-xs font-normal text-gray-dark">
                {unapplicabilityReason}
              </p>
            </div>
          )}
        </button>
      </SuggestedMethodWrapper>

      <GenericDialog
        isOpen={openDialog}
        translateAxis="y"
        modalType="GIFT_CARDS"
        dialogOverlay={true}
        customClass="overflow-scroll md:!top-auto md:absolute">
        <GiftCardDialog setIsOpen={(value: boolean) => setOpenDialog(value)} />
      </GenericDialog>

      {isLoading && <OverlaySpinner />}
    </>
  );
};

export default GiftCard;
